import { useState } from "react";
import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { ImageLightbox } from "../../components/ImageLightbox";
import { InventoryTabs } from "../../components/InventoryTabs";
import { LoadFailed, Loading } from "../../components/LoadState";
import { PanelHead } from "../../components/PanelHead";
import { ReviewGroupRow, ReviewSummary } from "../../components/ReviewGroupRow";
import { ScanCardsLink } from "../../components/ScanCardsLink";
import { ScanningScanRow } from "../../components/ScanningScanRow";
import { UnrecognisedScan } from "../../components/UnrecognisedScan";
import { useFocusOutlivesRows } from "../../components/use-focus-outlives-rows";
import { scanPhotos } from "../../core/primitives/card-display";
import {
  batchEntry,
  isCatalogDisputed,
  isCustomerGroup,
  reviewApprovesCodes,
  reviewEntry,
  reviewPriceText,
  reviewQuantity,
} from "../../core/primitives/review-row";
import { CONFIRM_ALL_KEY, DraftsActions, DraftsState, reviewShows } from "./drafts-model";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function DraftsPage() {
  const state = useSubtree(DraftsState);
  const actions = useSubtree(DraftsActions);
  const observer = useObserver();
  useFocusOutlivesRows();
  const [photos, setPhotos] = useState<string[] | null>(null);

  const status = observer.watch(state.status);
  const groups = observer.watch(state.groups);
  const unrecognised = observer.watch(state.unrecognised);
  const busyKey = observer.watch(state.busyKey);
  const actionError = observer.watch(state.actionError);
  const captures = observer.watch(state.captures);
  const drafts = observer.watch(state.drafts);
  const picking = observer.watch(state.picking);
  const pricedCount = groups.filter((g) => batchEntry(g, drafts[g.key]) !== null).length;

  const shows = reviewShows({
    status,
    groups: groups.length,
    captures: captures.length,
    unrecognised: unrecognised.length,
  });
  const copies = groups.reduce((n, g) => n + g.count, 0);

  return (
    <div className="mx-auto max-w-4xl px-2 pt-4 pb-24 sm:px-6 sm:pt-8 sm:pb-8 lg:px-8">
      <div className="panel p-4 sm:p-6">
        <PanelHead className="mb-6" action={<ScanCardsLink returnTo="/drafts" place="head" />}>
          <h1 className="text-lg font-bold text-white">Inventory</h1>
          <InventoryTabs active="review" reviewCount={groups.reduce((n, g) => n + g.count, 0)} />
        </PanelHead>

        {actionError && (
          <div className="note note-danger mb-4 flex items-center justify-between gap-3 text-danger">
            <span className="truncate">{actionError}</span>
            <button type="button" onClick={actions.dismissError} className="btn-ghost shrink-0">
              Dismiss
            </button>
          </div>
        )}

        {status.kind === "error" && (
          <LoadFailed title="Couldn't load the review queue." message={status.message} onRetry={actions.retry} />
        )}

        {shows.body === "loading" ? (
          <Loading label="Loading the review queue…" />
        ) : shows.body === "failed" ? null : shows.body === "empty" ? (
          <div className="py-10 text-center">
            <p className="text-base font-medium text-white">Nothing waiting on review</p>
            <p className="mx-auto mt-1 mb-4 max-w-xs text-sm text-muted">
              Scanned cards and boxes show up here. Price them, check the count, confirm.
            </p>
            <Link to="/scan" state={{ returnTo: "/drafts" }} className="btn-primary btn-sm">
              Start a scan
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {captures.map((capture) => (
              <ScanningScanRow
                key={capture.id}
                capture={capture}
                onRetry={() => actions.retryCapture(capture.id)}
                onDiscard={() => actions.discardCapture(capture.id)}
              />
            ))}

            {shows.readingUnder && <Loading label="Loading the review queue…" />}

            {groups.length > 0 && (
              <ReviewSummary
                detail={`${plural(groups.length, "item", "items")} · ${plural(copies, "copy", "copies")}`}
                pricedCount={pricedCount}
                confirming={busyKey === CONFIRM_ALL_KEY}
                disabled={busyKey !== null || picking !== null}
                onConfirmAll={() => void actions.confirmAllPriced()}
              />
            )}

            {groups.map((group) => (
              <ReviewGroupRow
                key={group.key}
                group={group}
                busy={busyKey === group.key || busyKey === CONFIRM_ALL_KEY || picking?.key === group.key}
                price={reviewPriceText(group, drafts[group.key])}
                quantity={reviewQuantity(group, drafts[group.key])}
                showQuantity={!isCustomerGroup(group)}
                canConfirm={reviewEntry(group, drafts[group.key]) !== null}
                pickingProductId={picking?.key === group.key ? picking.productId : null}
                approveCodes={reviewApprovesCodes(group, drafts[group.key])}
                catalogDisputed={isCatalogDisputed(group)}
                onApproveCodes={(approve) => actions.setApproveCodes(group, approve)}
                onPrice={(price) => actions.setPrice(group, price)}
                onQuantity={(quantity) => actions.setQuantity(group, quantity)}
                onPick={(productId) => void actions.pick(group, productId)}
                onConfirm={() => void actions.confirm(group)}
                onReview={() => actions.review(group)}
                onReject={() => void actions.reject(group)}
                onOpenImage={() => setPhotos(scanPhotos(group.sample))}
              />
            ))}
          </div>
        )}
        {unrecognised.length > 0 && (
          <section className="mt-8 space-y-2">
            <h2 className="text-base font-semibold text-white">Unrecognised scans ({unrecognised.length})</h2>
            {unrecognised.map((scan) => (
              <UnrecognisedScan key={scan.id} scan={scan} />
            ))}
          </section>
        )}
      </div>

      {photos && <ImageLightbox urls={photos} onClose={() => setPhotos(null)} />}
      <ScanCardsLink returnTo="/drafts" />
    </div>
  );
}
